import React, { ComponentType, FC, useMemo } from 'react';
import { ConnectedRouter, routerMiddleware, connectRouter } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import compose from '../../../utils/components/#compose';
import REDUX from '../../../constants/REDUX';
import { middlewares } from '../withStore';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import injectReducer from '../../redux/injectReducer';

export interface WithRouterProps {
  name: string;
  scope: string[];
  store: AugmentedStore<any>;
}

export interface WithRouterOptions { basename?: string }

export interface WithRouterType<T> {
  (WrappedComponent: ComponentType<T>): ComponentType<T & WithRouterProps>;
  (options: WithRouterOptions): (WrappedComponent: ComponentType<T>) => ComponentType<T & WithRouterProps>;
}

export interface WithRouterMock {}

/**
 * @since 0.2.0
 * @see See [App configuration](/Devguide/App%20configuration)
 * @component
 */
export default function withRouter<T>(WrappedComponentOrConfig: ComponentType<T> | WithRouterOptions) {
  if (typeof WrappedComponentOrConfig === 'function' || (WrappedComponentOrConfig as any).type || (WrappedComponentOrConfig as any).render) {
    return withRouterConfig(WrappedComponentOrConfig as FC<WithRouterProps>);
  }
  return (WrappedComponent: FC<WithRouterProps>) => withRouterConfig(WrappedComponent, WrappedComponentOrConfig);

  function withRouterConfig(WrappedComponent: FC<WithRouterProps>, { basename }: WithRouterOptions = {}) {
    const history = createBrowserHistory({
      // REACT_APP_BASENAME is deprecated by support is maintain for backward compatibility
      basename: basename || process.env.REACT_APP_BASENAME || process.env.PUBLIC_URL,
    });

    middlewares.push(routerMiddleware(history));

    const WithConnectedRouter = (props: WithRouterProps) => (
      <ConnectedRouter history={history}>
        <WrappedComponent {...props} />
      </ConnectedRouter>
    );

    const WithRouter = (props: WithRouterProps) => {
      const WithConnectedRouterAndReducer = useMemo(() => compose(
        injectReducer({
          key: REDUX.ROUTE_REDUCER_KEY,
          reducer: connectRouter(history),
        }),
      )(WithConnectedRouter), []);

      return <WithConnectedRouterAndReducer {...props} />;
    };

    WithRouter.displayName = getWrappedDisplayName('WithRouter', WrappedComponent);

    return hoistNonReactStatics(WithRouter, WrappedComponent);
  }
}
